import { create } from 'zustand';
import { formatFilter } from '@chess/api-games/filter';
import type { GameFilter, GameResult, GameStore, GameSummary } from '@chess/api-model';

// The list of games as the table shows it: this plugin's own state, one page
// of the game store's games (offset and size), the last 30 days, kept current
// by the store's subscription: a game an importer stores, a removal or a
// completed scan (the `blunder` filter's answer) reloads the page shown, so
// the table never needs telling. A page holds as many rows as fit on screen,
// so its size comes from the table's measurement and a page is named by its
// first row, not by a number.

export type Status = 'loading' | 'ready' | 'error';

/** The fewest rows a page shows, however little room there is; below it the screen scrolls. */
export const MIN_ROWS = 5;

interface State {
  /** The games of the page shown, newest first. */
  games: GameSummary[];
  /** The first row's place in the whole list, from 0. */
  offset: number;
  /** How many rows a page holds. */
  size: number;
  /** How many games the period holds in all, under the filter. */
  total: number;
  status: Status;
  error?: string;
  /** The filter the list is loaded for, from the library's URL. */
  filter: GameFilter;
  /** Loads the page starting at `offset`, `size` rows, and the total for a filter. */
  load(store: GameStore, filter: GameFilter, offset: number, size: number): void;
  /** The store changed: the page shown is read again. */
  apply(): void;
}

/**
 * Where a page may start: `offset` as asked, unless it is past the end, when
 * the last page starts instead (the rows a short last page holds, aligned
 * with paging from the first row).
 */
export function clampOffset(offset: number, size: number, total: number): number {
  if (offset <= 0 || total === 0) return 0;
  if (offset < total) return offset;
  return lastOffset(size, total);
}

/** Where the last page starts, paging from the first row. */
export function lastOffset(size: number, total: number): number {
  return total === 0 ? 0 : Math.floor((total - 1) / size) * size;
}

/** The pages either side of the one shown: first, previous, next, last, each a first row from 0. */
export function steps(offset: number, size: number, total: number): { first: number; previous: number; next: number; last: number } {
  return {
    first: 0,
    previous: Math.max(0, offset - size),
    next: Math.min(offset + size, lastOffset(size, total)),
    last: lastOffset(size, total),
  };
}

/** The number a row shows: its place in the whole list, from 1. */
export function rowNumber(offset: number, index: number): number {
  return offset + index + 1;
}

/** The query parameter naming the page shown by its first row, from 1 like the row numbers; absent on the first page. */
export const FROM_PARAM = 'from';

/** The first row (from 0) a library URL query asks for; the first one when absent or not a row number. */
export function offsetOf(search: string): number {
  const n = Number(new URLSearchParams(search).get(FROM_PARAM));
  return Number.isInteger(n) && n >= 1 ? n - 1 : 0;
}

/** The query for a page of a filter's list: the filter's own query plus `from`, left out on the first page. */
export function fromQuery(filterQuery: string, offset: number): string {
  const params = new URLSearchParams(filterQuery);
  if (offset > 0) params.set(FROM_PARAM, String(offset + 1));
  const query = params.toString();
  return query ? `?${query}` : '';
}

/** "41–60 of 121": the rows a page shows, from 1, and the total. */
export function rangeLabel(offset: number, shown: number, total: number): string {
  if (shown === 0) return `0 of ${total}`;
  return `${offset + 1}–${offset + shown} of ${total}`;
}

/** How many rows fit in `available` pixels of rows `rowHeight` high; never fewer than MIN_ROWS. */
export function rowsThatFit(available: number, rowHeight: number): number {
  if (!(rowHeight > 0)) return MIN_ROWS;
  return Math.max(MIN_ROWS, Math.floor(available / rowHeight));
}

/** The store the list loads from, kept for reloads its subscription triggers. */
let source: GameStore | undefined;

export const useGameList = create<State>((set, get) => ({
  games: [],
  offset: 0,
  size: MIN_ROWS,
  total: 0,
  status: 'loading',
  filter: {},
  load(store, filter, offset, size) {
    source = store;
    const status = store.getState().status;
    if (status !== 'ready') {
      // The page asked for is kept, so the load the store's readiness triggers asks for it again.
      set({ filter, size, games: [], total: 0, offset, status: status === 'failed' ? 'error' : 'loading', error: status === 'failed' ? 'the games could not be read' : undefined });
      return;
    }
    // From memory: a `blunder` is resolved from the facts the store holds.
    const query = { filter: store.resolveFilter(filter) };
    const total = store.count(query);
    const at = clampOffset(offset, size, total);
    const rows = store.list({ order: 'newest', offset: at, size, ...query });
    set({ filter, games: rows, total, offset: at, size, status: 'ready', error: undefined });
  },
  apply() {
    const { filter, offset, size } = get();
    if (source) get().load(source, filter, offset, size);
  },
}));

/** Loads a page for a filter and keeps the list following the store until unsubscribed. */
export function follow(store: GameStore, filter: GameFilter, offset: number, size: number): () => void {
  useGameList.getState().load(store, filter, offset, size);
  return store.subscribe(() => useGameList.getState().apply());
}


export type Outcome = 'won' | 'lost' | 'draw' | 'unknown';

/** How the game went for its owner, from the result and which side they played. */
export function outcome(game: Pick<GameSummary, 'owner' | 'white' | 'black' | 'result'>): Outcome {
  const owner = game.owner.toLowerCase();
  const side = game.white.name.toLowerCase() === owner ? 'white' : game.black.name.toLowerCase() === owner ? 'black' : undefined;
  const result: GameResult = game.result;
  if (result === '1/2-1/2') return 'draw';
  if (!side || result === '*') return 'unknown';
  const whiteWon = result === '1-0';
  return (side === 'white') === whiteWon ? 'won' : 'lost';
}

/** A player as a row shows it: name, and the rating if known. */
export function playerLabel(player: GameSummary['white']): string {
  return player.rating ? `${player.name} (${player.rating})` : player.name;
}

/** The day a game was played, in the reader's locale. */
export function formatDate(iso: string, locale?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
}
